import { ArrowLeftRight, Loader2 } from 'lucide-react'
import { lazy, Suspense, useMemo, useState } from 'react'

import type { BookAiRelation } from '@/api/services/book-ai'
import { cn } from '@/lib/utils'

import { type GlossaryEntry, largestRelationGroup } from './book-ai-position'
import { glossaryKindConfig } from './glossary-kinds'

const RelationGraphCanvas = lazy(() => import('./relation-graph-canvas'))

type Props = {
  relations: BookAiRelation[]
  entries: GlossaryEntry[]
}

// つながった人のまとまりがこの人数に満たない本（2 人ずつの関係ばかり）は、図にしても散らばるだけなので一覧にする
const MIN_GROUP_FOR_GRAPH = 4
const GRAPH_HEIGHT = 360

type PersonRelations = { name: string; kind: string; items: { other: string; relation: string }[] }

/** 人物ごとの関係（関係の多い人から） */
const groupByPerson = (relations: BookAiRelation[], kinds: Map<string, string>): PersonRelations[] => {
  const people = new Map<string, PersonRelations>()
  const add = (name: string, other: string, relation: string) => {
    const person = people.get(name) ?? { name, kind: kinds.get(name) ?? 'person', items: [] }
    person.items.push({ other, relation })
    people.set(name, person)
  }
  for (const r of relations) {
    add(r.source, r.target, r.relation)
    add(r.target, r.source, r.relation)
  }
  return [...people.values()].sort((a, b) => b.items.length - a.items.length)
}

const RelationList = ({ people }: { people: PersonRelations[] }) => (
  <ul className="divide-y">
    {people.map((person) => {
      const kind = glossaryKindConfig(person.kind)
      return (
        <li key={person.name} className="flex gap-2.5 py-2">
          <span className={cn('mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full', kind.className)}>
            <kind.icon className="size-3.5" />
          </span>
          <div className="min-w-0 flex-1 space-y-1">
            <p className="text-sm font-semibold wrap-break-word">{person.name}</p>
            <div className="flex flex-wrap gap-1">
              {person.items.map((item, i) => (
                <span key={i} className="flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs">
                  <ArrowLeftRight className="size-3 text-muted-foreground" />
                  {item.other}
                  <span className="text-muted-foreground">（{item.relation}）</span>
                </span>
              ))}
            </div>
          </div>
        </li>
      )
    })}
  </ul>
)

/**
 * 人物相関（関係がつながっている本は力学モデルの図、2 人ずつの関係ばかりの本は人物ごとの一覧）
 * 図では人物をタップすると、その人の関係を強調して下に一覧する
 */
export const RelationGraph = ({ relations, entries }: Props) => {
  const [selected, setSelected] = useState<string | null>(null)
  const kinds = useMemo(() => new Map(entries.map((e) => [e.name, e.kind])), [entries])
  const people = useMemo(() => groupByPerson(relations, kinds), [relations, kinds])
  const showGraph = useMemo(() => largestRelationGroup(relations) >= MIN_GROUP_FOR_GRAPH, [relations])

  if (relations.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">まだ人物の関係が分かっていません</p>
  }

  if (!showGraph) {
    return (
      <div className="space-y-2">
        <p className="text-xs text-muted-foreground">関係のつながりが少ないため、図ではなく人物ごとに表示しています</p>
        <RelationList people={people} />
      </div>
    )
  }

  const selectedPerson = people.find((p) => p.name === selected)

  return (
    <div className="space-y-3">
      <Suspense
        fallback={
          <div className="flex items-center justify-center" style={{ height: GRAPH_HEIGHT }}>
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        }
      >
        <RelationGraphCanvas
          relations={relations}
          kinds={kinds}
          height={GRAPH_HEIGHT}
          selected={selected}
          onSelect={setSelected}
        />
      </Suspense>
      {selectedPerson ? (
        <RelationList people={[selectedPerson]} />
      ) : (
        <p className="text-center text-xs text-muted-foreground">
          人物をタップすると、その人の関係を表示します（二本指で拡大すると関係の言葉も出ます）
        </p>
      )}
    </div>
  )
}
